import { FC, useState } from 'react';
import {
  CheckoutStates,
  getOrderStepModes,
  OrderErrorModal,
  OrderFailureTypes,
  OrderInfoConfirmation,
  OrderLoadingModal,
  OrderTermsConditions,
  Styled,
} from './';
import {
  OrderAddress,
  OrderPayment,
  OrderShippingMethod,
  OrderStep,
  OrderSummary,
} from 'components';
import {
  usePlaceOrder,
  useSaveOrderAddress,
  useSaveOrderShippingMethod,
} from 'reactQuery/mutations';

import { Address, CouponClient, CreditCardClientData } from 'types';
import {
  useAddresses,
  useDoctorProfile,
  usePromotionList,
  useRetriveCart,
} from 'reactQuery/queries';
import { useComponentVisible } from 'utils/hooks';
import { useI18n } from 'contexts/i18n';
import { useRouter } from 'next/router';
import getConfig from 'next/config';
import { ThreeDSecurePayment } from './ThreeDSecurePayment';
import { isCartContainsSubscriptionProducts } from './OrderCheckout.helper';
import { findSerialNumber } from './CurrentStep';
import { isValidBillingAddressSelected } from 'components/OrderAddress';
import SubscriptionWarningModal from './SubscriptionWarningModal';

const OrderCheckout: FC<{ ipAddress: string; setErr: any }> = ({
  ipAddress,
  setErr,
}) => {
  const { data: doctorProfile } = useDoctorProfile();
  const { data: currentCart } = useRetriveCart();
  const { data: address } = useAddresses();
  const [is3DSModalOpen, set3DSModalOpen] = useState(false);
  const [orderStep, setOrderStep] = useState<CheckoutStates>('address');
  const [orderFailure, setOrderFailure] = useState<OrderFailureTypes>('');
  const i18n = useI18n();
  const router = useRouter();
  const [selectedShippingAddress, setShippingAddress] = useState<Address>();

  const [selectedBillingAddress, setBillingAddress] = useState<Address>();

  const [email, setEmail] = useState<string>(currentCart?.email ?? '');

  const [selectedCreditCard, setCreditCard] = useState<
    CreditCardClientData | undefined
  >();
  const [isPayByInvoiceSelected, setIsPayByInvoiceSelected] = useState(false);

  const [isAcceptedBrandGuildlines, setIsAcceptedBrandGuildlines] =
    useState(false);
  const [isAcceptedUsageGuildlines, setIsAcceptedUsageGuildlines] =
    useState(false);

  const saveOrderAddressMutation = useSaveOrderAddress();
  const saveOrderShippingMethodMutation = useSaveOrderShippingMethod();
  // setErr(saveOrderAddressMutation?.error?.response?.data.error);

  const { publicRuntimeConfig } = getConfig();
  const threeDSCountryList = publicRuntimeConfig.COUNTRIES_WITH_3DS.split(',');
  const storeRegion = publicRuntimeConfig.STORE_REGION;

  const is3DSecurePaymentEnabled =
    threeDSCountryList.length > 0 &&
    threeDSCountryList.includes(selectedBillingAddress?.countryIso);

  const placeOrderMutation = usePlaceOrder();

  const orderStepModes = getOrderStepModes(orderStep);

  const { data } = usePromotionList();

  const {
    ref: ModalRef,
    isComponentVisible: showSubscriptionModal,
    setIsComponentVisible: setShowSubscriptionModal,
  } = useComponentVisible(false);

  const freeShippingCoupon = data?.productsList
    .flatMap(p => p.discounts)
    .find(
      (discount): discount is CouponClient =>
        discount.discountType === 'coupon' && discount.type === 'Shipping'
    );

  const isCartHavingSubscriptionProducts = currentCart?.lineItems
    ? isCartContainsSubscriptionProducts(currentCart.lineItems)
    : false;

  const mutationCallbacks = {
    onError: (error: any) => {
      const apiError = error?.response?.data?.error;
      const errorMessage =
        typeof apiError === 'string'
          ? apiError
          : i18n[
              'There was an issue calculating tax. Please try again later.'
            ] ?? 'There was an issue calculating tax. Please try again later.';
      setErr(null);
      setTimeout(() => setErr(errorMessage), 0);
    },
    onSuccess: () => {
      setErr(undefined);
    },
  };

  const handleAddressChange = () => {
    if (
      selectedShippingAddress &&
      isValidBillingAddressSelected(
        selectedBillingAddress,
        address?.billingAddresses
      )
    ) {
      saveOrderAddressMutation.mutate(
        {
          shipAddressId: selectedShippingAddress.id,
          billAddressId: selectedBillingAddress?.id,
          email: email,
          setOrderStep,
          setShowSubscriptionModal,
        },
        mutationCallbacks
      );
    }
  };

  const handleShippingMethod = () => {
    saveOrderShippingMethodMutation.mutate(
      {
        setOrderStep,
        freeShippingCoupon,
      },
      mutationCallbacks
    );
  };

  const handlePaymentMethod = () => {
    setOrderStep('confirm');
  };

  const handlePlaceOrder = () => {
    if (storeRegion === 'NA' || storeRegion === 'LATAM') {
      const isZeroPricedOrder = currentCart && currentCart.total == 0;
      if (is3DSecurePaymentEnabled && !isZeroPricedOrder) {
        set3DSModalOpen(true);
      } else {
        placeOrderMutation.mutate(
          {
            setOrderStep: setOrderStep,
            selectedCreditCard: selectedCreditCard,
            isPayByInvoiceSelected: isPayByInvoiceSelected,
            setOrderFailure: setOrderFailure,
            ipAddress: ipAddress,
            pcUsername: doctorProfile?.pcUserName,
          },
          mutationCallbacks
        );
      }
    }

    if (
      storeRegion === 'EMEA' ||
      storeRegion === 'GCC' ||
      storeRegion === 'APAC'
    ) {
      const isZeroPricedOrder = currentCart && currentCart.total == 0;
      const selectedCreditCardType = selectedCreditCard?.cardType || '';
      const isSEPAOrACHCard = ['SEPA', 'ACH'].includes(selectedCreditCardType);
      if (
        is3DSecurePaymentEnabled &&
        !isPayByInvoiceSelected &&
        !isZeroPricedOrder &&
        !isSEPAOrACHCard
      ) {
        set3DSModalOpen(true);
      } else {
        placeOrderMutation.mutate(
          {
            setOrderStep,
            selectedCreditCard,
            isPayByInvoiceSelected,
            setOrderFailure,
            ipAddress: ipAddress,
            pcUsername: doctorProfile?.pcUserName,
          },
          mutationCallbacks
        );
      }
    }
  };

  const handleEdit = (step: CheckoutStates) => {
    setOrderStep(step);
  };

  return (
    <Styled.CheckoutContainer>
      <Styled.CheckoutWrapper>
        {orderFailure === 'GENERIC_ORDER_ERROR' && (
          <OrderErrorModal
            onClose={() => setOrderFailure('')}
          ></OrderErrorModal>
        )}

        {is3DSModalOpen && (
          <ThreeDSecurePayment
            creditCard={selectedCreditCard}
            setOrderStep={setOrderStep}
            setOrderFailure={setOrderFailure}
            set3DSModalOpen={set3DSModalOpen}
            placeOrderMutation={placeOrderMutation}
          />
        )}

        {placeOrderMutation.isLoading && <OrderLoadingModal />}
        <Styled.CheckoutStepsContainer>
          {orderStep === 'confirm' && (
            <OrderInfoConfirmation
              onPlaceOrder={handlePlaceOrder}
              isLoading={placeOrderMutation.isLoading}
              isAcceptedBrandGuildlines={isAcceptedBrandGuildlines}
              isAcceptedUsageGuildlines={isAcceptedUsageGuildlines}
              setIsAcceptedBrandGuildlines={setIsAcceptedBrandGuildlines}
              setIsAcceptedUsageGuildlines={setIsAcceptedUsageGuildlines}
            />
          )}
          <OrderStep
            title={i18n['1. Shipping & Billing']}
            mode={orderStepModes.address}
            onEdit={() => handleEdit('address')}
          >
            <OrderAddress
              mode={orderStepModes.address}
              onSave={handleAddressChange}
              selectedShippingAddress={selectedShippingAddress}
              setShippingAddress={setShippingAddress}
              selectedBillingAddress={selectedBillingAddress}
              setBillingAddress={setBillingAddress}
              isSaveAddressMutationLoading={saveOrderAddressMutation.isLoading}
              email={email}
              onEmailChange={event => setEmail(event.currentTarget.value)}
            />
            {showSubscriptionModal && (
              <SubscriptionWarningModal
                onCloseClickHandler={() => {
                  setShowSubscriptionModal(false);
                }}
                ref={ModalRef}
              />
            )}
          </OrderStep>
          {currentCart && currentCart.checkoutStates.includes('delivery') && (
            <OrderStep
              title={`${findSerialNumber(
                currentCart?.checkoutStates ?? [''],
                'delivery'
              )} . ${i18n['Shipping Method']}`}
              mode={orderStepModes.delivery}
              onEdit={() => handleEdit('delivery')}
            >
              <OrderShippingMethod
                mode={orderStepModes.delivery}
                onSubmit={handleShippingMethod}
                isSaveShippingRateMutationLoading={
                  saveOrderShippingMethodMutation.isLoading
                }
                isOrderEligibleForFreeShipping={
                  typeof freeShippingCoupon !== 'undefined'
                }
              />
            </OrderStep>
          )}

          {currentCart && currentCart.checkoutStates.includes('payment') && (
            <OrderStep
              title={`${findSerialNumber(
                currentCart?.checkoutStates,
                'payment'
              )} . ${i18n['Payment']}`}
              mode={orderStepModes.payment}
              onEdit={() => handleEdit('payment')}
            >
              <OrderPayment
                mode={orderStepModes.payment}
                onSubmit={handlePaymentMethod}
                selectedCreditCard={selectedCreditCard}
                setCreditCard={setCreditCard}
                orderFailure={orderFailure}
                setOrderFailure={setOrderFailure}
                isPayByInvoiceSelected={isPayByInvoiceSelected}
                setIsPayByInvoiceSelected={setIsPayByInvoiceSelected}
                isCartHavingSubscriptionProducts={
                  isCartHavingSubscriptionProducts
                }
              />
            </OrderStep>
          )}

          {currentCart && currentCart.checkoutStates.includes('confirm') ? (
            <OrderStep
              title={`${findSerialNumber(
                currentCart?.checkoutStates ?? [''],
                'confirm'
              )} . ${i18n['Review']}`}
              mode={orderStepModes.confirm}
              onEdit={() => {
                return;
              }}
            >
              <OrderTermsConditions
                onPlaceOrder={handlePlaceOrder}
                isLoading={placeOrderMutation.isLoading}
                isAcceptedBrandGuildlines={isAcceptedBrandGuildlines}
                isAcceptedUsageGuildlines={isAcceptedUsageGuildlines}
                setIsAcceptedBrandGuildlines={setIsAcceptedBrandGuildlines}
                setIsAcceptedUsageGuildlines={setIsAcceptedUsageGuildlines}
              />
            </OrderStep>
          ) : (
            currentCart &&
            currentCart.checkoutStates.includes('complete') && (
              <OrderStep
                title={`${findSerialNumber(
                  currentCart.checkoutStates,
                  'complete'
                )}. ${i18n['Review']}`}
                mode={orderStepModes.confirm}
                onEdit={() => {
                  return;
                }}
              >
                <OrderTermsConditions
                  onPlaceOrder={handlePlaceOrder}
                  isLoading={placeOrderMutation.isLoading}
                  isAcceptedBrandGuildlines={isAcceptedBrandGuildlines}
                  isAcceptedUsageGuildlines={isAcceptedUsageGuildlines}
                  setIsAcceptedBrandGuildlines={setIsAcceptedBrandGuildlines}
                  setIsAcceptedUsageGuildlines={setIsAcceptedUsageGuildlines}
                />
              </OrderStep>
            )
          )}
          {/* <OrderStep
            title={`${findSerialNumber(
              currentCart?.checkoutStates ?? [''],
              'confirm'
            )} . Review`}
            mode={orderStepModes.confirm}
            onEdit={() => {
              return;
            }}
          >
            <OrderTermsConditions
              onPlaceOrder={handlePlaceOrder}
              isLoading={placeOrderMutation.isLoading}
            />
          </OrderStep> */}
        </Styled.CheckoutStepsContainer>
        <Styled.OrderSummaryContainer>
          <OrderSummary orderStep={orderStep} />
        </Styled.OrderSummaryContainer>
      </Styled.CheckoutWrapper>
    </Styled.CheckoutContainer>
  );
};

export default OrderCheckout;
